import { translate } from 'vue-gettext'

const { pgettext } = translate

/**
 * Email Job Types Configuration
 * Each job type defines:
 * - value: unique identifier for the job type
 * - label: translated display name
 * - description: brief description of what the job does
 * - emailTypes: array of email type keys this job will send
 * - jobSettings: configuration for BaseJobSettings component
 *   - showScheduleFrom, showScheduleTo, showSendTime, showSendEvery: boolean to show/hide fields
 *   - sendEveryDefault: default value for send_every (in hours)
 *   - sendEveryReadonly: make send_every field readonly
 *   - sendEveryLabel, sendEveryPlaceholder, sendEveryHelpText: custom labels
 *   - helpText: general help text displayed at the top
 *   - customParameters: array of custom parameter fields
 *     Each parameter: { key, name, hint, type, default, options (for select) }
 */
const EmailJobTypes = {
    // ========================================
    // CRUISE-SPECIFIC JOBS
    // ========================================

    CRUISE_AFTER_STAY: {
        value: 'cruise_after_stay',
        jobClass: 'CruiseAfterStayJob',
        label: pgettext('email_scheduler.job_type', 'Cruise - After Stay'),
        description: pgettext('email_scheduler.job_type_desc', 'Send email to cruise passengers N days after their disembarkation'),
        emailTypes: ['CRUISE_AFTER_STAY'],
        jobSettings: {
            showScheduleFrom: false,
            showScheduleTo: false,
            showSendTime: true,
            showSendEvery: false,
            sendEveryDefault: 24,
            helpText: pgettext('email_scheduler.job_settings', 'This job runs once daily at the specified time. It processes cruise passengers in batches and sends the first contact email after their stay ends.'),
            customParameters: [
                {
                    key: 'days_after_eos_min',
                    name: pgettext('email_scheduler.job_settings', 'Min Days After Disembarkment'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Minimum days after the passenger disembarks (end of stay)'),
                    type: 'number',
                    default: 5
                },
                {
                    key: 'days_after_eos_max',
                    name: pgettext('email_scheduler.job_settings', 'Max Days After Disembarkment'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Maximum days after the passenger disembarks (end of stay)'),
                    type: 'number',
                    default: 20
                },
                {
                    key: 'batch_size',
                    name: pgettext('email_scheduler.job_settings', 'Batch Size'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Number of passengers to process per run (default: 100)'),
                    type: 'number',
                    default: 100
                }
            ]
        }
    },

    CRUISE_SECOND_CONTACT: {
        value: 'cruise_second_contact',
        jobClass: 'CruiseSecondContactJob',
        label: pgettext('email_scheduler.job_type', 'Cruise - Second Contact'),
        description: pgettext('email_scheduler.job_type_desc', 'Send follow-up email to cruise passengers N days after disembarkation (different messages for those with/without orders)'),
        emailTypes: ['CRUISE_SECOND_CONTACT_WITH_ORDERS', 'CRUISE_SECOND_CONTACT_NO_ORDERS'],
        jobSettings: {
            showScheduleFrom: false,
            showScheduleTo: false,
            showSendTime: true,
            showSendEvery: false,
            sendEveryDefault: 24,
            helpText: pgettext('email_scheduler.job_settings', 'This job runs once daily at the specified time. It automatically sends different messages: thank-you emails for passengers who purchased, reminder emails for those who haven\'t.'),
            customParameters: [
                {
                    key: 'days_after_eos_min',
                    name: pgettext('email_scheduler.job_settings', 'Min Days After Disembarkment'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Minimum days after the passenger disembarks (end of stay)'),
                    type: 'number',
                    default: 5
                },
                {
                    key: 'days_after_eos_max',
                    name: pgettext('email_scheduler.job_settings', 'Max Days After Disembarkment'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Maximum days after the passenger disembarks (end of stay)'),
                    type: 'number',
                    default: 20
                },
                {
                    key: 'batch_size',
                    name: pgettext('email_scheduler.job_settings', 'Batch Size'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Number of passengers to process per run (default: 100)'),
                    type: 'number',
                    default: 100
                }
            ]
        }
    },

    // ========================================
    // MEDIA & CHECKIN JOBS
    // ========================================

    NEW_MEDIA_FOR_CHECKIN: {
        value: 'new_media_for_checkin',
        jobClass: 'NewMediaForCheckinJob',
        label: pgettext('email_scheduler.job_type', 'New Media for Check-in'),
        description: pgettext('email_scheduler.job_type_desc', 'Notify users when new photos/videos are available for their check-in'),
        emailTypes: ['NEW_MEDIA_FOR_CHECKIN'],
        jobSettings: {
            showScheduleFrom: true,
            showScheduleTo: true,
            showSendTime: true,
            showSendEvery: true,
            sendEveryDefault: 2,
            sendEveryLabel: pgettext('email_scheduler.job_settings', 'Check Every (hours)'),
            sendEveryPlaceholder: pgettext('email_scheduler.job_settings', 'Hours between checks'),
            sendEveryHelpText: pgettext('email_scheduler.job_settings_hint', 'How often to check for new media and send notifications'),
            helpText: pgettext('email_scheduler.job_settings', 'Configure when and how often to check for new media and notify users. The job tracks the last delivery ID to detect new uploads.'),
            customParameters: []
        }
    },

    // ========================================
    // KPI & ANALYTICS JOBS
    // ========================================

    KPI_HIGHLIGHTS: {
        value: 'kpi_highlights',
        jobClass: 'KpiHighlightsJob',
        label: pgettext('email_scheduler.job_type', 'KPI Highlights'),
        description: pgettext('email_scheduler.job_type_desc', 'Send event performance metrics and highlights to specified users'),
        emailTypes: ['KPI_HIGHLIGHTS'],
        jobSettings: {
            showScheduleFrom: true,
            showScheduleTo: true,
            showSendTime: true,
            showSendEvery: true,
            sendEveryDefault: 24,
            sendEveryLabel: pgettext('email_scheduler.job_settings', 'Send Every (hours)'),
            sendEveryPlaceholder: pgettext('email_scheduler.job_settings', 'Hours between reports'),
            sendEveryHelpText: pgettext('email_scheduler.job_settings_hint', 'Recommended: 24 hours (daily) or 168 hours (weekly)'),
            helpText: pgettext('email_scheduler.job_settings', 'Sends KPI reports to users with access to the stats page. Recipients must have the appropriate permissions.'),
            customParameters: [
                {
                    key: 'emails',
                    name: pgettext('email_scheduler.job_settings', 'Recipient Emails'),
                    hint: pgettext('email_scheduler.job_settings_hint', 'Comma-separated list of email addresses to receive KPI reports'),
                    type: 'text',
                    default: ''
                },
                {
                    key: 'kpi_activations',
                    name: pgettext('email_scheduler.job_settings', 'Activations'),
                    type: 'boolean',
                    default: true
                },
                {
                    key: 'kpi_avg_photo_per_user',
                    name: pgettext('email_scheduler.job_settings', 'Avg Photos per User'),
                    type: 'boolean',
                    default: true
                },
                {
                    key: 'kpi_content_promoters',
                    name: pgettext('email_scheduler.job_settings', 'Content Promoters'),
                    type: 'boolean',
                    default: true
                }
            ]
        }
    },
}

export default EmailJobTypes

/**
 * Get job type configuration by value
 * @param {string} value - The job type value
 * @returns {Object|undefined} The job type configuration
 */
export const getJobTypeByValue = (value) => {
    return Object.values(EmailJobTypes).find(type => type.value === value)
}

/**
 * Get job type configuration by job class
 * @param {string} jobClass - The job class name (e.g., 'CruiseAfterStayJob')
 * @returns {Object|undefined} The job type configuration
 */
export const getJobTypeByClass = (jobClass) => {
    return Object.values(EmailJobTypes).find(type => type.jobClass === jobClass)
}

/**
 * Get all job type options for select dropdown
 * @returns {Array} Array of {value, label} objects where value is the jobClass
 */
export const getJobTypeOptions = () => {
    return Object.values(EmailJobTypes).map(type => ({
        value: type.jobClass,
        label: type.label
    }))
}

/**
 * Get email types for a specific job type
 * @param {string} jobTypeOrClass - The job type value or class name
 * @returns {Array} Array of email type keys
 */
export const getEmailTypesForJob = (jobTypeOrClass) => {
    let jobType = getJobTypeByClass(jobTypeOrClass)
    if (!jobType) {
        jobType = getJobTypeByValue(jobTypeOrClass)
    }
    return jobType ? jobType.emailTypes : []
}

/**
 * Get job type label
 * @param {string} jobTypeOrClass - The job type value or class name
 * @returns {string} The translated label or the value itself
 */
export const getJobTypeLabel = (jobTypeOrClass) => {
    // Try to find by class first, then by value
    let jobType = getJobTypeByClass(jobTypeOrClass)
    if (!jobType) {
        jobType = getJobTypeByValue(jobTypeOrClass)
    }
    return jobType ? jobType.label : jobTypeOrClass
}

/**
 * Get job settings configuration for a specific job type
 * @param {string} jobTypeOrClass - The job type value or class name
 * @returns {Object|null} Job settings config object or null
 */
export const getJobSettingsConfig = (jobTypeOrClass) => {
    let jobType = getJobTypeByClass(jobTypeOrClass)
    if (!jobType) {
        jobType = getJobTypeByValue(jobTypeOrClass)
    }
    return jobType ? jobType.jobSettings : null
}

/**
 * Get job class name for backend from value
 * @param {string} jobTypeValue - The job type value (e.g., 'cruise_after_stay')
 * @returns {string|null} The job class name (e.g., 'CruiseAfterStayJob') or null
 */
export const getJobClass = (jobTypeValue) => {
    const jobType = getJobTypeByValue(jobTypeValue)
    return jobType ? jobType.jobClass : null
}

/**
 * Get job type value from job class name
 * @param {string} jobClass - The job class name (e.g., 'CruiseAfterStayJob')
 * @returns {string|null} The job type value (e.g., 'cruise_after_stay') or null
 */
export const getValueFromJobClass = (jobClass) => {
    const jobType = Object.values(EmailJobTypes).find(type => type.jobClass === jobClass)
    return jobType ? jobType.value : null
}
